import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Pencil,
  Delete,
  Package,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Upload,
  CheckSquare,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Tag,
  Building2,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { productosApi, parametrosApi } from '../../utils/api';
import CabysExplorer from '../../components/CabysExplorer';
import useAuthStore from '../../store/authStore';

// Toolbar button component
function ToolbarButton({ icon: Icon, label, onClick, disabled, variant = 'default' }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500",
    success: "border border-green-400 text-green-600 hover:bg-green-50 hover:border-green-500",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <Icon className="h-5 w-5 mb-1" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

const UNIDADES_MEDIDA = {
  'Unid': 'Unidad',
  'Kg': 'Kilogramo',
  'g': 'Gramo',
  'L': 'Litro',
  'mL': 'Mililitro',
  'm': 'Metro',
  'cm': 'Centímetro',
  'm2': 'Metro cuadrado',
  'm3': 'Metro cúbico',
  'Sp': 'Servicio Profesional',
  'Os': 'Otros Servicios',
  'h': 'Hora',
  'd': 'Día'
};

export default function ListaProductos() {
  const navigate = useNavigate();
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [sortField, setSortField] = useState('nombre');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showMasiveAction, setShowMasiveAction] = useState(null); // 'iva' | 'activate' | 'deactivate' | 'delete'
  const [masiveValue, setMasiveValue] = useState('');
  const [showCabysExplorer, setShowCabysExplorer] = useState(false);
  const [impuestosDisponibles, setImpuestosDisponibles] = useState([]);
  const [impuestosSeleccionados, setImpuestosSeleccionados] = useState([]);

  // Copia entre empresas (mismo tenant). Solo visible si el usuario tiene
  // acceso a más de 1 emisor — se obtiene del store de auth.
  const { emisores, currentEmisor } = useAuthStore();
  const otrosEmisores = (emisores || []).filter(e => e.id !== currentEmisor?.id);
  const [showCopiarEmpresaModal, setShowCopiarEmpresaModal] = useState(false);
  const [emisoresDestino, setEmisoresDestino] = useState([]);
  const [copiandoEmpresa, setCopiandoEmpresa] = useState(false);
  // 27/10/2026: paginación. Antes se pedían solo los primeros 100 y el resto no aparecía.
  const POR_PAGINA = 50;
  const [pagina, setPagina] = useState(1);
  const [paginacion, setPaginacion] = useState(null);

  useEffect(() => {
    cargarImpuestos();
  }, []);

  const cargarImpuestos = async () => {
    try {
      const response = await parametrosApi.listTarifasIva();
      setImpuestosDisponibles(response.data?.data || []);
    } catch (e) { /* ignore */ }
  };

  const cargarProductos = async (search = '', pag = pagina) => {
    setLoading(true);
    try {
      const response = await productosApi.list({ q: search, page: pag, limit: POR_PAGINA });
      setProductos(response.data?.data || []);
      setPaginacion(response.data?.pagination || null);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  // Búsqueda con espera: una búsqueda nueva vuelve a la página 1
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagina !== 1) setPagina(1); else cargarProductos(searchTerm, 1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Cambio de página
  useEffect(() => {
    cargarProductos(searchTerm, pagina);
  }, [pagina]);

  // Copia el producto seleccionado a uno o varios emisores destino del mismo tenant.
  // Backend replica TODO: codigo, nombre, descripción, CABYS, unidad, precio, impuestos.
  // Si el código interno ya existe en el destino, lo salta (resultado 'duplicado').
  const handleCopiarAEmpresa = async () => {
    if (selectedIds.length !== 1 || emisoresDestino.length === 0) return;
    setCopiandoEmpresa(true);
    try {
      const res = await productosApi.copyToEmisor(selectedIds[0], emisoresDestino);
      const resultados = res.data?.data?.resultados || [];
      const ok = resultados.filter(r => r.status === 'copiado');
      const dup = resultados.filter(r => r.status === 'duplicado');
      if (ok.length > 0) toast.success(`Copiado a ${ok.length} empresa(s): ${ok.map(r => r.nombre).join(', ')}`);
      if (dup.length > 0) toast(`Ya existía en ${dup.length} empresa(s): ${dup.map(r => r.nombre).join(', ')}`, { icon: 'ℹ️' });
      setShowCopiarEmpresaModal(false);
      setEmisoresDestino([]);
      setSelectedIds([]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al copiar producto');
    } finally {
      setCopiandoEmpresa(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await productosApi.delete(id);
      setProductos(prev => (prev || []).filter(p => p.id !== id));
      setShowDeleteModal(null);
      toast.success('Producto eliminado');
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === sortedProductos.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(sortedProductos.map(p => p.id));
    }
  };

  const handleMasiveAction = async (action, value) => {
    if (selectedIds.length === 0) return;
    try {
      const response = await productosApi.masiveUpdate({ ids: selectedIds, action, value });
      const result = response.data?.data || response.data;
      toast.success(`${result?.updated || selectedIds.length} productos actualizados`);
      setSelectedIds([]);
      setShowMasiveAction(null);
      setMasiveValue('');
      cargarProductos(searchTerm);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al actualizar productos');
    }
  };

  const productosArray = Array.isArray(productos) ? productos : [];
  const sortedProductos = [...productosArray].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';

    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const formatCurrency = (amount, moneda = 'CRC') => {
    if (!amount && amount !== 0) return '-';
    const symbol = moneda === 'USD' ? '$' : '₡';
    return `${symbol}${new Intl.NumberFormat('es-CR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)}`;
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ?
      <ChevronUp className="h-4 w-4 inline" /> :
      <ChevronDown className="h-4 w-4 inline" />;
  };

  return (
    <div className="flex flex-col gap-4 h-[var(--alto-contenido)] min-h-0">
      {/* Toolbar */}
      <div className="bg-white rounded-lg border p-3 relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre, código o CABYS..."
              className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-52 text-sm"
            />
          </div>
          <span className="text-sm text-gray-500">{paginacion?.total ?? productosArray.length} prod.</span>
        </div>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <ToolbarButton
            icon={Plus}
            label="Nuevo Producto"
            onClick={() => navigate('/productos/nuevo')}
            variant="primary"
          />
          <ToolbarButton
            icon={RefreshCw}
            label="Actualizar"
            onClick={() => cargarProductos(searchTerm)}
            disabled={loading}
          />
        </div>

        {/* Bulk actions bar */}
        {selectedIds.length > 0 && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
            <span className="text-sm text-blue-600 font-medium">{selectedIds.length} seleccionados</span>
            <button
              onClick={() => navigate(`/productos/nuevo?copyFrom=${selectedIds[0]}`)}
              disabled={selectedIds.length !== 1}
              className="px-2 py-1 text-xs border border-purple-400 text-purple-600 rounded hover:bg-purple-50 disabled:opacity-40 disabled:cursor-not-allowed"
              title={selectedIds.length === 1 ? 'Crear un producto nuevo usando este como plantilla' : 'Seleccione solo 1 producto para copiar'}
            >Copiar</button>
            {/* Copia a otra empresa del mismo tenant — solo aparece si el usuario
                tiene acceso a más de 1 emisor activo. Replica producto completo. */}
            {otrosEmisores.length > 0 && (
              <button
                onClick={() => { setEmisoresDestino([]); setShowCopiarEmpresaModal(true); }}
                disabled={selectedIds.length !== 1}
                className="px-2 py-1 text-xs border border-indigo-400 text-indigo-600 rounded hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                title={selectedIds.length === 1 ? 'Copiar este producto al inventario de otra empresa' : 'Seleccione solo 1 producto'}
              >
                <Building2 size={12} /> Copiar a Empresa
              </button>
            )}
            <button onClick={() => { setImpuestosSeleccionados([]); setShowMasiveAction('impuestos'); }} className="px-2 py-1 text-xs border border-green-400 text-green-600 rounded hover:bg-green-50">Impuestos</button>
            <button onClick={() => setShowCabysExplorer(true)} className="px-2 py-1 text-xs border border-blue-400 text-blue-600 rounded hover:bg-blue-50">CABYS</button>
            <button onClick={() => setShowMasiveAction('activate')} className="px-2 py-1 text-xs border border-emerald-400 text-emerald-600 rounded hover:bg-emerald-50">Activar</button>
            <button onClick={() => setShowMasiveAction('deactivate')} className="px-2 py-1 text-xs border border-amber-400 text-amber-600 rounded hover:bg-amber-50">Desactivar</button>
            <button onClick={() => setShowMasiveAction('delete')} className="px-2 py-1 text-xs border border-red-400 text-red-600 rounded hover:bg-red-50">Eliminar</button>
            <button onClick={() => setSelectedIds([])} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">✕</button>
          </div>
        )}
      </div>

      {/* Table: llena el alto útil, hace scroll adentro y la paginación queda al pie */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 min-h-0 flex flex-col">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            Cargando productos...
          </div>
        ) : productosArray.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay productos</h3>
            <p className="text-gray-500 mb-4">Comience agregando su primer producto o servicio</p>
            <button
              onClick={() => navigate('/productos/nuevo')}
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50"
            >
              <Plus className="h-4 w-4" />
              Agregar Producto
            </button>
          </div>
        ) : (
          <div className="overflow-auto flex-1 min-h-0">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === sortedProductos.length && sortedProductos.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-400"
                    />
                  </th>
                  <th
                    className="text-left px-4 py-3 font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('codigo')}
                  >
                    Código <SortIcon field="codigo" />
                  </th>
                  <th
                    className="text-left px-4 py-3 font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('descripcion')}
                  >
                    Descripción <SortIcon field="descripcion" />
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">
                    CABYS
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">
                    Unidad
                  </th>
                  <th
                    className="text-right px-4 py-3 font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('precio')}
                  >
                    Precio <SortIcon field="precio" />
                  </th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">
                    IVA
                  </th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">
                    Inventario
                  </th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">
                    Activo
                  </th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700 w-24">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedProductos.map((producto) => (
                  <tr
                    key={producto.id}
                    className={`transition-colors ${selectedIds.includes(producto.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                  >
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(producto.id)}
                        onChange={() => toggleSelect(producto.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    {/* Código */}
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                        {producto.codigo || '-'}
                      </span>
                    </td>

                    {/* Descripción */}
                    {/* 27/10/2026: hasta 2 líneas en un ancho acotado (cabe en el alto que ya tiene la fila); el texto
                        completo queda en el title. El tema pone nowrap en las celdas: aquí se anula. */}
                    <td className="px-4 py-2 min-w-[16rem] max-w-[26rem] whitespace-normal">
                      <div className="font-medium text-gray-900 leading-snug whitespace-normal" title={producto.descripcion || producto.nombre || ''}
                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {producto.descripcion || producto.nombre || '-'}
                      </div>
                    </td>

                    {/* CABYS */}
                    <td className="px-4 py-3">
                      {producto.codigoCabys ? (
                        <div>
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-mono">
                            {producto.codigoCabys}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    {/* Unidad */}
                    <td className="px-4 py-3 text-gray-600">
                      {UNIDADES_MEDIDA[producto.unidadMedida] || producto.unidadMedida || 'Unid'}
                    </td>

                    {/* Precio */}
                    <td className="px-4 py-3 text-sm text-right">
                      {formatCurrency(producto.precio, producto.moneda)}
                    </td>

                    {/* IVA */}
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        producto.tarifaIva > 0
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {producto.tarifaIva || 0}%
                      </span>
                    </td>

                    {/* Inventario (esServicio = false significa que SÍ es inventario) */}
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        !producto.esServicio
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {!producto.esServicio ? 'Sí' : 'No'}
                      </span>
                    </td>

                    {/* Activo */}
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        producto.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {producto.isActive ? 'Sí' : 'No'}
                      </span>
                    </td>

                    {/* Acciones */}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => navigate(`/productos/${producto.id}/editar`)}
                          className="p-1.5 hover:bg-blue-50 rounded transition-colors"
                          title="Editar"
                        >
                          <Pencil className="h-5 w-5 text-blue-500" />
                        </button>
                        <button
                          onClick={() => setShowDeleteModal(producto)}
                          className="p-1.5 hover:bg-red-50 rounded transition-colors"
                          title="Eliminar"
                        >
                          <Delete className="h-5 w-5 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {paginacion && paginacion.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 shrink-0">
            <div className="text-sm text-gray-500">
              Mostrando {((paginacion.page - 1) * paginacion.limit) + 1} a {Math.min(paginacion.page * paginacion.limit, paginacion.total)} de {paginacion.total} productos
            </div>
            {paginacion.pages > 1 && (
              <div className="flex items-center gap-2">
                <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina <= 1 || loading}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40" aria-label="Página anterior">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-700 tabular-nums">Página {paginacion.page} de {paginacion.pages}</span>
                <button onClick={() => setPagina(p => Math.min(paginacion.pages, p + 1))} disabled={pagina >= paginacion.pages || loading}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40" aria-label="Página siguiente">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Eliminar Producto</h2>
            <p className="text-gray-600 mb-4">
              ¿Está seguro que desea eliminar <strong>{showDeleteModal.nombre}</strong>?
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(showDeleteModal.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Masive action modal */}
      {showMasiveAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                {showMasiveAction === 'impuestos' && 'Asignar Impuestos'}
                {showMasiveAction === 'activate' && 'Activar Productos'}
                {showMasiveAction === 'deactivate' && 'Desactivar Productos'}
                {showMasiveAction === 'delete' && 'Eliminar Productos'}
              </h2>
              <p className="text-sm text-gray-500">{selectedIds.length} productos seleccionados</p>
            </div>
            <div className="p-4">
              {showMasiveAction === 'impuestos' && (
                <div className="space-y-3">
                  {(() => {
                    const ivaImpuestos = impuestosDisponibles.filter(i => i.codigoImpuesto === '01' || i.codigo_impuesto === '01');
                    const otrosImpuestos = impuestosDisponibles.filter(i => i.codigoImpuesto !== '01' && i.codigo_impuesto !== '01');
                    return (
                      <>
                        {ivaImpuestos.length > 0 && (
                          <div>
                            <div className="text-xs font-semibold text-blue-700 mb-2">IVA</div>
                            <div className="flex flex-wrap gap-2">
                              {ivaImpuestos.map(imp => (
                                <label key={imp.id} className="flex items-center gap-1.5 cursor-pointer bg-white px-2 py-1 rounded border hover:bg-blue-50">
                                  <input
                                    type="checkbox"
                                    checked={impuestosSeleccionados.includes(imp.id)}
                                    onChange={() => setImpuestosSeleccionados(prev =>
                                      prev.includes(imp.id) ? prev.filter(id => id !== imp.id) : [...prev.filter(id => !ivaImpuestos.map(i => i.id).includes(id)), imp.id]
                                    )}
                                    className="rounded border-gray-300 text-blue-600"
                                  />
                                  <span className="text-xs">{imp.descripcion}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                        {otrosImpuestos.length > 0 && (
                          <div>
                            <div className="text-xs font-semibold text-red-700 mb-2">Otros Impuestos</div>
                            <div className="flex flex-wrap gap-2">
                              {otrosImpuestos.map(imp => (
                                <label key={imp.id} className="flex items-center gap-1.5 cursor-pointer bg-white px-2 py-1 rounded border hover:bg-red-50">
                                  <input
                                    type="checkbox"
                                    checked={impuestosSeleccionados.includes(imp.id)}
                                    onChange={() => setImpuestosSeleccionados(prev =>
                                      prev.includes(imp.id) ? prev.filter(id => id !== imp.id) : [...prev, imp.id]
                                    )}
                                    className="rounded border-gray-300 text-red-600"
                                  />
                                  <span className="text-xs">{imp.descripcion} ({imp.porcentaje}%)</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
              {showMasiveAction === 'activate' && (
                <p className="text-gray-700">¿Desea activar <strong>{selectedIds.length}</strong> productos seleccionados?</p>
              )}
              {showMasiveAction === 'deactivate' && (
                <p className="text-amber-700">¿Desea desactivar <strong>{selectedIds.length}</strong> productos seleccionados? No aparecerán en la búsqueda al facturar.</p>
              )}
              {showMasiveAction === 'delete' && (
                <p className="text-red-600">¿Está seguro que desea eliminar <strong>{selectedIds.length}</strong> productos? Esta acción no se puede deshacer.</p>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowMasiveAction(null); setMasiveValue(''); }}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (showMasiveAction === 'impuestos') {
                    if (impuestosSeleccionados.length === 0) { toast.error('Seleccione al menos un impuesto'); return; }
                    handleMasiveAction('impuestos', impuestosSeleccionados);
                  } else {
                    handleMasiveAction(showMasiveAction);
                  }
                }}
                className={`px-4 py-2 rounded-lg font-medium border-2 ${
                  showMasiveAction === 'delete' ? 'border-red-500 text-red-600 hover:bg-red-50' :
                  showMasiveAction === 'deactivate' ? 'border-amber-500 text-amber-600 hover:bg-amber-50' :
                  showMasiveAction === 'activate' ? 'border-green-500 text-green-600 hover:bg-green-50' :
                  'border-blue-500 text-blue-600 hover:bg-blue-50'
                }`}
              >
                {showMasiveAction === 'delete' ? 'Eliminar' :
                 showMasiveAction === 'activate' ? 'Activar' :
                 showMasiveAction === 'deactivate' ? 'Desactivar' : 'Aplicar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CABYS Explorer for masive assign */}
      <CabysExplorer
        isOpen={showCabysExplorer}
        onClose={() => setShowCabysExplorer(false)}
        onSelect={(cabys) => {
          setShowCabysExplorer(false);
          handleMasiveAction('cabys', cabys.codigo);
        }}
      />

      {/* Modal copiar producto a otra(s) empresa(s) del mismo tenant.
          Backend replica TODO: códigos, descripción, CABYS, unidad, precio,
          impuestos, tarifa IVA, esServicio, etc. Si el `código` interno ya
          existe en el destino, se salta y se reporta como duplicado. */}
      {showCopiarEmpresaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-900">Copiar a Otra Empresa</h2>
              </div>
              <button onClick={() => setShowCopiarEmpresaModal(false)} className="p-1 hover:bg-white/60 rounded">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-3">
                Se copiará el producto seleccionado al inventario de la(s) empresa(s) que elijas.
                Si ya existe un producto con el mismo código en el destino, se salta automáticamente.
              </p>
              <p className="text-xs text-gray-500 mb-3">
                Se copia: código, nombre, descripción, CABYS, unidad de medida, moneda, precio,
                tarifa IVA, código de impuesto y todos los impuestos asociados.
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2">
                {otrosEmisores.map(e => (
                  <label key={e.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={emisoresDestino.includes(e.id)}
                      onChange={(ev) => {
                        if (ev.target.checked) setEmisoresDestino(prev => [...prev, e.id]);
                        else setEmisoresDestino(prev => prev.filter(id => id !== e.id));
                      }}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <Building2 className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{e.nombre}</span>
                    {e.identificacion && (
                      <span className="text-xs text-gray-500 ml-auto">{e.identificacion}</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowCopiarEmpresaModal(false)}
                disabled={copiandoEmpresa}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCopiarAEmpresa}
                disabled={emisoresDestino.length === 0 || copiandoEmpresa}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {copiandoEmpresa && <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />}
                Copiar a {emisoresDestino.length || 0} empresa(s)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
